import React, { useState, useRef, ReactNode } from "react";
import {
  Box,
  Menu,
  MenuItem,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  useTheme,
} from "@mui/material";
import { Avatar } from "components";

const MenuTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    padding: 0,
    border: "1px solid #dadde9",
    borderRadius: "8px",
  },
}));

export interface MenuOption {
  name?: string;
  value?: string;
  type?: string;
  icon?: React.ReactNode;
  avatar?: boolean;
  avatarData?: {
    full_name: string;
    profile_image: string;
  };
  disabled?: boolean;
  // Optional secondary line under `name`.
  description?: string;
  // Optional hover tooltip on the menu row. When the option is `disabled`,
  // MUI swallows pointer events on `MenuItem`, so the row is wrapped in a
  // `<span>` (which DOES receive pointer events) so the tooltip still fires.
  tooltip?: string;
}

interface HoverableMenuProps {
  options: MenuOption[];
  onOptionClick?: (option: MenuOption) => void;
  children: React.ReactNode;
  selectedValue?: string;
  menuListSx?: any;
  menuItemSx?: any;
  tooltipSx?: any;
  isDisabled?: boolean;
  /**
   * Replaces the whole row, not just its content (unlike SelectField's
   * builder). `onOptionClick`, `disabled` and `tooltip` don't apply to built
   * rows — use it for headers and view-only lists.
   */
  menuItemBuilder?: ({
    item,
    index,
  }: {
    item: MenuOption;
    index: number;
  }) => ReactNode;
}

export const HoverableMenu = ({
  options,
  onOptionClick,
  children,
  selectedValue,
  menuListSx,
  menuItemSx,
  tooltipSx,
  isDisabled = false,
  menuItemBuilder,
}: HoverableMenuProps) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 500); // Small delay to allow mouse to move to menu
  };

  const handleOptionClick = (e: React.MouseEvent, option: MenuOption) => {
    e.stopPropagation();
    onOptionClick?.(option);
    setIsOpen(false);
  };

  const menuContent = (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        padding: "8px 0px",
        minWidth: "175px",
        backgroundColor: "#FFF",
        borderRadius: "8px",
        ...menuListSx,
      }}
    >
      {options.map((option, index) => {
        // Custom rows (headers, read-only lists) bypass the clickable MenuItem.
        if (menuItemBuilder) {
          return (
            <React.Fragment key={option.type || index}>
              {menuItemBuilder({ item: option, index })}
            </React.Fragment>
          );
        }

        const menuItem = (
          <MenuItem
            key={option.type || index}
            onClick={(e) => handleOptionClick(e, option)}
            disabled={option?.disabled || false}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor:
                option.value === selectedValue
                  ? theme.palette.neutral["B-005"]
                  : "transparent",
              "&:hover": {
                backgroundColor: theme.palette.neutral["B-005"],
              },
              ...theme.typography["body-small"],
              ...menuItemSx,
            }}
          >
            {/* {option.icon} */}
            <Box
              sx={{
                display: "flex",
                flexGrow: 1,
                direction: "row",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {(option?.avatar || option?.icon) && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    // Grows for larger icons (e.g. a 32px icon circle).
                    minWidth: "25px",
                    minHeight: "25px",
                  }}
                >
                  {option?.avatar ? (
                    <Avatar
                      src={option?.avatarData?.profile_image}
                      fullname={option?.avatarData?.full_name || ""}
                      sx={{
                        width: "24px",
                        height: "24px",
                      }}
                    />
                  ) : (
                    option?.icon
                  )}
                </Box>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                <Typography
                  variant="body-small"
                  sx={{ color: theme.palette.text.primary }}
                >
                  {option.name}
                </Typography>
                {option.description && (
                  <Typography
                    variant="body-xsmall"
                    sx={{ color: theme.palette.lightBg.low }}
                  >
                    {option.description}
                  </Typography>
                )}
              </Box>
            </Box>
          </MenuItem>
        );

        // When an MUI MenuItem is disabled, pointer events are swallowed and a
        // wrapping Tooltip won't fire. Wrap the disabled item in a `<span>`
        // (which DOES receive pointer events) so the tooltip still shows.
        if (option.tooltip) {
          return (
            <Tooltip
              key={option.type || index}
              title={option.tooltip}
              placement="left"
            >
              <span style={{ display: "block" }}>{menuItem}</span>
            </Tooltip>
          );
        }

        return menuItem;
      })}
    </Box>
  );
  return (
    <MenuTooltip
      open={!isDisabled && isOpen}
      onClose={() => setIsOpen(false)}
      title={menuContent}
      placement="bottom-start"
      // The trigger is a focusable Box (tabIndex/role=button), so CLICKING it
      // focuses it. MUI Tooltip's focus listener then fires onClose on the
      // subsequent blur (leaveDelay 0) the instant you mousedown a menu item —
      // unmounting the popper before the item's click completes, so the menu
      // closes without running the action. Open/close is driven entirely by our
      // own hover/click handlers, so the focus listener adds nothing but this
      // bug. Disable it.
      disableFocusListener
      // arrow
      PopperProps={{
        sx: {
          "& .MuiTooltip-tooltip": {
            position: "relative",
            "&::before": {
              // prettier-ignore
              content: '""',
              position: "absolute",
              top: "-12px", // Adjust based on your arrow size
              left: 0,
              right: 0,
              height: "12px",
              background: "transparent",
            },
          },
          ...tooltipSx,
        },
      }}
    >
      <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </Box>
    </MenuTooltip>
  );
};

{
  /* <MenuTooltip
open={!isDisabled && isOpen}
onClose={() => setIsOpen(false)}
title={menuContent}
placement="bottom-start"
PopperProps={{
  sx: {
    "& .MuiTooltip-tooltip": {
      position: "relative",
      margin: "10px !important",
      transform: "none !important",
    },
    "& .MuiTooltip-arrow": {
      display: "none",
    },
    ...tooltipSx,
  },
}}
></MenuTooltip> */
}
